import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import { AppStore } from "../app/store";
import { ActionType } from "../@types";
import { Button, Dialog } from "@cmsgov/ds-medicare-gov";
import { hasLocalSession } from "../helpers/loginHelpers";
import { differenceInMilliseconds } from "date-fns";
import { sendKeepAlive } from "../api";
import { withRouter, RouteComponentProps } from "react-router";
import { logMinutesFromMs, logout, useTranslate } from "../helpers";
import { FormattedMessage } from "react-intl-phraseapp";
import { ApiError, ApiResponseError } from "../helpers/errors";
import routes from "../app/routes";
import {
  AnalyticsActionType,
  AnalyticsButtonStyle,
  AnalyticsButtonType,
} from "../app/contexts/Analytics/types";

const UserActivityMonitor: FunctionComponent<RouteComponentProps> = props => {
  const { state, dispatch } = useContext(AppStore.AppContext);
  const { csr, beneficiary, lastActivity } = state;
  const loggedIn = hasLocalSession();

  const [showInactiveDialog, setShowInactiveDialog] = useState(false);
  const [inactivityTimeout, setInactivityTimeout] = useState<
    NodeJS.Timeout | undefined
  >(undefined);
  const [logoutTimeout, setLogoutTimeout] = useState<
    NodeJS.Timeout | undefined
  >(undefined);
  const [sendingKeepAlive, setSendingKeepAlive] = useState(false);

  const t = useTranslate();

  const activityEvents = ["click", "mousemove", "keydown", "wheel", "scroll"];

  const oneMinuteInMs = 60 * 1000;
  const csrInactiveTimeoutMs = oneMinuteInMs * 15;
  const beneInactiveTimeoutMs = oneMinuteInMs * 10;
  const logoutTimeoutInMs = oneMinuteInMs * 5;
  const timeToResetLastActivity = oneMinuteInMs;

  const inactiveTimeoutInMsRef = useRef(beneInactiveTimeoutMs);
  const inactiveTimeoutMinsRef = useRef(
    logMinutesFromMs(beneInactiveTimeoutMs)
  );

  const lastActivityRef = useRef(lastActivity);
  const loggedInRef = useRef(loggedIn);

  const getSinceLastActivity = () =>
    lastActivityRef.current
      ? differenceInMilliseconds(new Date(), new Date(lastActivityRef.current))
      : 0;

  const onLogout = () => setShowInactiveDialog(false);

  const keepAlive = async (): Promise<void> => {
    if (loggedInRef.current) {
      setSendingKeepAlive(true);

      try {
        await sendKeepAlive();
      } catch (_e) {
        const e = _e as ApiError;

        if (e instanceof ApiResponseError) {
          const { info } = e;
          const { apiResponseStatus } = info;

          if (apiResponseStatus == 401) {
            logout({
              apiExpired: true,
              clientExpired: true,
              dispatch,
              onLogout,
            });
          }
        }
      }

      setSendingKeepAlive(false);
    }
  };

  /**
   * Resets a user's last activity based on a listener detecting one of several events
   * Detects whether it's been at least a minute since the last reset
   * @see activityEvents
   */
  const handleActivity = (): void => {
    const curr = new Date();
    const sinceLastActivity = getSinceLastActivity();

    if (
      !lastActivityRef.current ||
      (lastActivityRef.current && sinceLastActivity >= timeToResetLastActivity)
    ) {
      if (!sendingKeepAlive) {
        dispatch({ type: ActionType.UPDATE_LAST_ACTIVITY, payload: curr });

        keepAlive();
      }
    }
  };

  const handleNoActivity = (): void => {
    setShowInactiveDialog(true);

    setLogoutTimeout(
      setTimeout(
        () =>
          logout({
            apiExpired: false,
            clientExpired: true,
            dispatch,
            onLogout,
          }),
        logoutTimeoutInMs
      )
    );
  };

  // Every time there is activity, we want to reset the activity timer
  useEffect(() => {
    lastActivityRef.current = lastActivity;

    if (loggedIn && !showInactiveDialog) {
      if (inactivityTimeout) {
        clearTimeout(inactivityTimeout);
      }

      setInactivityTimeout(
        setTimeout(handleNoActivity, inactiveTimeoutInMsRef.current)
      );
    }
  }, [lastActivity]);

  const clearTimeouts = (): void => {
    if (inactivityTimeout) {
      clearTimeout(inactivityTimeout);
      setInactivityTimeout(undefined);
    }

    if (logoutTimeout) {
      clearTimeout(logoutTimeout);
      setLogoutTimeout(undefined);
    }
  };

  /**
   * This method handles the case where a user still has an active session,
   * but they've been away from MCT for longer than the inactive timeout.
   * This is called when they access MCT again and we will log them out.
   */
  const handleStaleSession = async (): Promise<void> => {
    const doNotRetrieveMissingBeneRoutes = [
      routes.csrLanding,
      routes.mbpLandingPage,
      routes.slsCallback,
    ];

    const doNotRetrieveRoute = doNotRetrieveMissingBeneRoutes.includes(
      props.location.pathname
    );

    const sinceLastActivity = lastActivity
      ? differenceInMilliseconds(new Date(), new Date(lastActivity))
      : 0;

    if (loggedIn && !beneficiary && !doNotRetrieveRoute) {
      const loginCallbackRoute = {
        pathname: `${routes.slsCallback}`,
        state: { from: `${props.location.pathname}${props.location.search}` },
      };

      props.history.push(loginCallbackRoute);
    } else {
      if (!loggedIn && beneficiary) {
        dispatch({ type: ActionType.RESET_STATE });
      }
      if (lastActivity && loggedIn && beneficiary) {
        if (sinceLastActivity >= inactiveTimeoutInMsRef.current) {
          logout({
            apiExpired: false,
            clientExpired: true,
            dispatch,
            onLogout,
          });
        }
      }
    }
  };

  useEffect(() => {
    handleStaleSession();

    // We always want to record the last activity even if the user isn't logged in
    activityEvents.forEach(eventType =>
      window.addEventListener(eventType, handleActivity)
    );

    return (): void => {
      activityEvents.forEach(eventType =>
        window.removeEventListener(eventType, handleActivity)
      );
    };
  }, []);

  useEffect(() => {
    const hasSessionWithBeneficiary = loggedIn && !!beneficiary;
    loggedInRef.current = hasSessionWithBeneficiary;

    // Clear the timeouts because CSRs and beneficiaries have different timeout values.
    clearTimeouts();

    // When the user logs in, start recording activity
    if (hasSessionWithBeneficiary) {
      inactiveTimeoutInMsRef.current = csr
        ? csrInactiveTimeoutMs
        : beneInactiveTimeoutMs;

      inactiveTimeoutMinsRef.current = logMinutesFromMs(
        inactiveTimeoutInMsRef.current
      );

      setInactivityTimeout(
        setTimeout(handleNoActivity, inactiveTimeoutInMsRef.current)
      );
      keepAlive();
    }
  }, [loggedIn, beneficiary, csr]);

  const continueSession = (): void => {
    setShowInactiveDialog(false);

    keepAlive();

    clearTimeouts();
  };

  return (
    <>
      {showInactiveDialog && (
        <Dialog
          getApplicationNode={(): HTMLElement | null =>
            document.getElementById("App")
          }
          escapeExits={false}
          className="UserActivityMonitor__modal"
          heading={t("user_activity.title")}
          onExit={continueSession}
          actions={[
            <Button
              variation="solid"
              onClick={() => {
                dispatch({
                  type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
                  settings: {
                    button: {
                      buttonStyle: AnalyticsButtonStyle.PRIMARY,
                      buttonType: AnalyticsButtonType.BUTTON,
                      text: t("user_activity.continue_session"),
                    },
                  },
                });
                continueSession();
              }}
              key="primary"
            >
              {t("user_activity.continue_session")}
            </Button>,
            <Button
              variation="ghost"
              key="cancel"
              onClick={(): Promise<void> => {
                dispatch({
                  type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
                  settings: {
                    button: {
                      buttonStyle: AnalyticsButtonStyle.TRANSPARENT,
                      buttonType: AnalyticsButtonType.BUTTON,
                      text: t("user_activity.logout"),
                    },
                  },
                });
                // apiExpired and clientExpired both default to `false`
                // This actively logs out MCTAPI and MBP (is a user logout)
                return logout({
                  dispatch,
                  onLogout,
                });
              }}
            >
              {t("user_activity.logout")}
            </Button>,
          ]}
        >
          <FormattedMessage
            id="user_activity.inactive_message"
            values={{
              inactivityMinutes: Math.floor(
                inactiveTimeoutInMsRef.current / 1000 / 60
              ),
              logoutMinutes: Math.floor(logoutTimeoutInMs / 1000 / 60),
            }}
          />
        </Dialog>
      )}
      {props.children}
    </>
  );
};

export default withRouter(UserActivityMonitor);
